/**
 * Pure helpers of the plans and stamps screens (PLAN.md 4.24). The estimate here is only a hint
 * shown before sending: the server decides the real cost inside the insert (a simultaneous reply can
 * change it), and its answer, 402 `no_stamps` included, is what the screen finally follows.
 */
import type { BillingSummary, Catalogue, CataloguePlan, EntitlementView } from "./api";

export const MIB = 1024 * 1024;
export const GIB = 1024 * MIB;

/**
 * Stamps a send will use: one per recipient wrap, two with a reply envelope (the capsule and a stamp
 * for the answer), nothing for the author's own copy. A reply to an envelope: the first one of this
 * member is paid by the request's sender (0), a later one costs 1; a reply that carries an envelope of
 * its own adds 1. A group sums its wraps.
 */
export function estimateStamps(input: { wraps: number; envelope: boolean; reply?: { first: boolean } | null }): number {
  if (input.wraps <= 0) return 0;
  const each = input.reply ? (input.reply.first ? 0 : 1) + (input.envelope ? 1 : 0) : input.envelope ? 2 : 1;
  return input.wraps * each;
}

/** Whether a send whose estimate is `cost` is certainly not affordable: a zero-cost send always is. */
export function knownShortage(cost: number, balance: number | null): boolean {
  return balance !== null && cost > balance;
}

/** Width of a meter bar in percent, clamped to 0..100; the numbers beside it are never clamped. */
export function meterPercent(used: number, total: number): number {
  if (!(total > 0)) return used > 0 ? 100 : 0;
  return Math.max(0, Math.min(100, Math.round((used / total) * 100)));
}

/** A random key for one click that creates something; kept until it succeeds, so a retry is the same request. */
export function newIdempotencyKey(prefix = "k"): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return `${prefix}-${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
}

/** Binary sizes as the quotas are written: "25 MiB", "1 GiB", "256 KiB". */
export function sizeLabel(bytes: number): string {
  if (bytes >= GIB && bytes % (GIB / 4) === 0) return `${trim(bytes / GIB)} GiB`;
  if (bytes >= MIB) return `${trim(bytes / MIB)} MiB`;
  return `${trim(bytes / 1024)} KiB`;
}
const trim = (n: number) => String(Math.round(n * 100) / 100);

/**
 * A provider amount in the currency's smallest unit (cents for EUR, yen for JPY) as text: the number
 * of decimals is the currency's own (PLAN.md 4.27 R12).
 */
export function priceLabel(minor: number, currency: string, locale: string): string {
  const format = new Intl.NumberFormat(locale, { style: "currency", currency });
  return format.format(minor / 10 ** (format.resolvedOptions().maximumFractionDigits ?? 2));
}

/** First moment of the next UTC month after `now` (seconds): when the monthly stamps refresh. */
export function nextRefresh(now: number): number {
  const d = new Date(now * 1000);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1) / 1000;
}

/**
 * Would a Start subscription add anything to what the member has? Start is hidden when it would not
 * (Legacy members), unless they already hold a purchased Start term, where another one extends it.
 * The provider would sell it anyway; this only keeps a useless purchase off the cards.
 */
export function startHelps(summary: BillingSummary, start: CataloguePlan | undefined, entitlements: EntitlementView[], now: number): boolean {
  if (!start) return false;
  if (entitlements.some((e) => e.source === "subscription" && e.plan_id === "start" && (e.ends_at ?? Infinity) > now)) return true;
  return start.monthly_units > summary.plan.monthly_units || start.attachment_bytes > summary.plan.attachment_bytes || start.base_storage_bytes > summary.storage.quota_bytes;
}

/** Stamps still unspent as a share of the month's grant, for the meter. */
export function periodUsedPercent(summary: BillingSummary): number {
  return meterPercent(summary.stamps.period.consumed, summary.stamps.period.granted);
}

/**
 * Money really moves: a real provider in its live environment (reviews/06-action-plan.md 3.5).
 * Until then members see what the plans will be, without a Buy button; admins keep the buttons to
 * test the sandbox (PLAN.md 4.27 step 6).
 */
export function sellingLive(catalogue: Pick<Catalogue, "provider" | "environment">): boolean {
  return catalogue.provider !== "fake" && catalogue.environment === "live";
}
